import { toError } from 'pixai-utils/error'
import type { ErrorResponse } from 'react-router'
import { isRouteErrorResponse } from 'react-router'

/**
 * React Router throws plain `ErrorResponse` objects (`{ status, statusText,
 * data, internal }`) for route-level 4xx/5xx — they have no `message` or
 * `stack`, so passing them to Sentry produces opaque "Object captured as
 * exception" issues that cannot be grouped or symbolicated.
 *
 * 4xx responses (404 unmatched, 401/403 auth, …) are user-visible navigation
 * outcomes, not bugs. Mirrors the `isInternalError` rule in
 * `pixai-server/src/api/graphql/plugin/handleError.ts`.
 */
export function isExpectedRouteError(error: unknown): error is ErrorResponse {
  return (
    isRouteErrorResponse(error) && error.status >= 400 && error.status < 500
  )
}

/**
 * Wrap a route error into a real `Error` so Sentry can group and stack-trace
 * it. Real `Error` instances pass through untouched.
 */
export function toReportableError(error: unknown): Error {
  if (isRouteErrorResponse(error)) {
    const wrapped = new Error(
      `Route error ${error.status}: ${error.statusText}`,
      { cause: error.data },
    )
    wrapped.name = 'RouteErrorResponse'
    return wrapped
  }

  return toError(error)
}
